// Э-5. Карточка заявки: параметры, рейсы со статусами, назначение машин, прогресс, история, задачи.

import { useEffect, useState } from "react";
import { setQuery } from "../../shared/router";
import { ApiError } from "../../shared/api";
import { AuditTable, type AuditRow } from "../../shared/audit";
import { fmtDate, fmtNum, fmtPhone, fmtTime, telHref, zonedISO } from "../../shared/format";
import { useLive } from "../../shared/live";
import { Bar, Button, Drawer, Field, Input, Loading, Modal, ReasonDialog, Select, Tabs, useAsync, useToast } from "../../shared/ui";
import { api, can, CHANNELS, live, ORDER_STATUS, ORDER_TONE, POUR, TRIP_ORDER, TRIP_STATUS, useCtx } from "../ctx";
import { PlaceDot } from "../pages/Fleet";
import { CloseTaskDialog, TaskDone, type TaskRow } from "./TaskClose";
import { OptionList, type Availability, type Option } from "./OrderForm";

type Trip = any;

export function OrderCard({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { me } = useCtx();
  const toast = useToast();
  const o = useAsync<any>(() => api.get(`/orders/${id}`), [id]);
  const [tab, setTab] = useState<"trips" | "history" | "tasks" | "files">("trips");
  const [dialog, setDialog] = useState<null | "cancel" | "partial" | "reschedule" | "edit" | "link">(null);
  useLive(live, ["order.", "trip."], () => o.reload());

  const [busy, setBusy] = useState(false);
  // О-3 (22.10.2026): решение лаборатории или выбор рецептуры меняют заявку, и у диспетчера оставалась
  // старая версия — «Подтвердить» отказывал. Для подтверждений берём текущую версию заявки прямо перед действием:
  // проверки (ждёт лабораторию, нет рейсов) всё равно делает сервер.
  const fresh = async () => (await api.get<any>(`/orders/${id}`)).version as number;
  const act = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast.ok(ok);
      onChanged();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
      o.reload();
    }
  };

  const d = o.data;
  const closed = d && ["done", "partial", "rescheduled", "cancelled"].includes(d.status);
  return (
    <Drawer title={d ? `Заявка №${d.number}` : "Заявка"} onClose={onClose}
      extra={d && <span className={`tag ${ORDER_TONE[d.status]}`}>{ORDER_STATUS[d.status]}</span>}
      footer={d && !closed && (
        <>
          {can.dispatch(me.role) && d.status === "new" && (d.lab_status === "pending"
            ? <span className="small muted" style={{ alignSelf: "center" }}>Подтвердить можно после решения лаборатории</span>
            : <Button variant="primary" disabled={busy} onClick={() => act(async () => api.post(`/orders/${id}/confirm`, { version: await fresh() }), "Заявка подтверждена")}>Подтвердить</Button>
          )}
          {can.lab(me.role) && d.lab_status === "pending" && (
            <>
              <Button variant="primary" disabled={busy} onClick={() => act(async () => api.post(`/orders/${id}/lab`, { version: await fresh(), approved: true }), "Марка подтверждена лабораторией")}>Лаборатория: подтвердить марку</Button>
              <Button variant="danger" disabled={busy} onClick={() => act(async () => api.post(`/orders/${id}/lab`, { version: await fresh(), approved: false }), "Марка отклонена")}>Отклонить</Button>
            </>
          )}
          {can.createOrder(me.role) && <Button onClick={() => setDialog("link")}>Ссылка для клиента</Button>}
          {can.createOrder(me.role) && (
            <>
              <Button onClick={() => setDialog("edit")}>Изменить</Button>
              <Button onClick={() => setDialog("reschedule")}>Перенести</Button>
              {d.status === "in_progress" && <Button onClick={() => setDialog("partial")}>Клиент остановил приём</Button>}
              <Button variant="danger" onClick={() => setDialog("cancel")}>Отменить заявку</Button>
            </>
          )}
        </>
      )}>
      {!d ? <Loading error={o.error} onRetry={o.reload} /> : (
        <div className="stack">
          <div className="cols-2">
            <div className="stack tight">
              <h3>{d.customer.name}</h3>
              <div>{d.site.name}{d.site.address ? `, ${d.site.address}` : ""}</div>
              {d.contact && <div className="small">{d.contact.name}{d.contact.position ? `, ${d.contact.position}` : ""} <a href={`tel:${d.contact.phone}`}>{d.contact.phone}</a></div>}
              <div className="small muted">Канал: {CHANNELS[d.channel]}{d.short_notice ? " · срочная (позже 2 дней)" : ""}{d.recurring_id ? " · по регулярному шаблону" : ""}</div>
              {d.status_reason && <div className="small">Причина: {d.status_reason}</div>}
            </div>
            <div className="stack tight">
              <div><b>{d.grade.code}</b>, <span className="num">{fmtNum(d.volume_m3)} м³</span>, {POUR[d.pour_method]}{d.intensity_m3h ? `, ${d.intensity_m3h} м³/ч` : ""}</div>
              <div className="num">{fmtDate(d.date)}, первая машина {fmtTime(d.desired_start)}{d.plant_name ? `, ${d.plant_name}` : ""}</div>
              {d.lab_status !== "not_required" && <div className="small">Лаборатория: {{ pending: "ждёт подтверждения", approved: "марка подтверждена", rejected: "марка отклонена" }[d.lab_status as string]}</div>}
              {d.price_m3 !== null && <div className="small muted">Цена {fmtNum(d.price_m3, 2)} ₽/м³{d.delivery_price ? `, доставка ${fmtNum(d.delivery_price, 2)} ₽` : ""}</div>}
              {(d.comment_driver || d.comment_operator) && (
                <div className="small">{d.comment_operator && <>Оператору: {d.comment_operator}<br /></>}{d.comment_driver && <>Водителю: {d.comment_driver}</>}</div>
              )}
            </div>
          </div>
          <div className="stack tight">
            <div className="row between small">
              <span>Отгружено <b className="num">{fmtNum(d.progress.shipped_m3)}</b>, в пути <b className="num">{fmtNum(d.progress.in_transit_m3)}</b>, осталось <b className="num">{fmtNum(d.progress.remaining_m3)} м³</b></span>
              <span className="num">{fmtNum(d.progress.pct, 0)}%</span>
            </div>
            <Bar value={d.progress.pct} />
          </div>
          <Quality order={d} onChanged={() => { o.reload(); onChanged(); }} />
          <Tabs value={tab} onChange={setTab} items={[{ value: "trips", label: `Рейсы (${d.trips.filter((t: Trip) => t.status !== "cancelled").length})` }, { value: "files", label: "Файлы" }, { value: "history", label: "История" }, { value: "tasks", label: "Задачи" }]} />
          {tab === "trips" && <TripsTable order={d} onChanged={() => { o.reload(); onChanged(); }} />}
          {tab === "history" && <History id={id} />}
          {tab === "tasks" && <Tasks orderId={id} />}
          {tab === "files" && <Files orderId={id} canEdit={can.createOrder(me.role)} />}
        </div>
      )}
      {dialog === "cancel" && d && (
        <ReasonDialog title={`Отменить заявку №${d.number}?`} action="Отменить заявку" danger onCancel={() => setDialog(null)}
          onConfirm={(reason) => act(() => api.post(`/orders/${id}/cancel`, { version: d.version, reason }), "Заявка отменена").then(() => setDialog(null))} />
      )}
      {dialog === "partial" && d && (
        <ReasonDialog title="Клиент остановил приём" action="Отменить остаток" danger onCancel={() => setDialog(null)}
          onConfirm={(reason) => act(() => api.post(`/orders/${id}/partial`, { version: d.version, reason }), "Остаток отменён").then(() => setDialog(null))} />
      )}
      {dialog === "link" && d && <ClientLink orderId={id} number={d.number} onClose={() => setDialog(null)} />}
      {dialog === "edit" && d && <EditOrder order={d} onClose={() => setDialog(null)} onDone={() => { setDialog(null); o.reload(); onChanged(); }} />}
      {dialog === "reschedule" && d && <Reschedule order={d} onClose={() => setDialog(null)} onDone={(nid) => { setDialog(null); onChanged(); if (nid !== id) setQuery({ order: nid }); else o.reload(); }} />}
    </Drawer>
  );
}

/** Этап 3: рецептура заявки (лаборатория) и паспорт качества. */
function Quality({ order, onChanged }: { order: any; onChanged: () => void }) {
  const { me } = useCtx();
  const toast = useToast();
  const lab = ["lab", "admin"].includes(me.role);
  const canPassport = ["lab", "admin", "dispatcher", "manager", "director"].includes(me.role);
  const issue = ["lab", "admin", "dispatcher", "manager"].includes(me.role);
  const pass = useAsync<any[]>(() => (canPassport ? api.get("/lab/passports", { order: order.id }) : Promise.resolve([])), [order.id, order.version]);
  const rec = useAsync<any>(() => (lab ? api.get("/lab/recipes", { grade: order.grade.id }) : Promise.resolve(null)), [order.grade.id, lab]);
  if (!canPassport && !lab) return null;
  const p = pass.data?.[0];
  const approved = (rec.data?.rows || []).filter((r: any) => r.status === "approved");
  const print = (pid: string) => api.open(`/lab/passports/${pid}/print`).catch((e) => toast.error(e));
  const make = async (reissue: boolean) => {
    try {
      const r = await api.post(`/orders/${order.id}/passport${reissue ? "?reissue=true" : ""}`);
      toast.ok(`Паспорт качества №${r.number}`);
      pass.reload();
      print(r.id);
    } catch (e) { toast.error(e); }
  };
  return (
    <div className="panel pad row" style={{ justifyContent: "space-between", alignItems: "center", gap: 12 }}>
      <div className="stack tight">
        <strong>Качество</strong>
        {lab && rec.data && (
          <div className="row tight small" style={{ alignItems: "center" }}>
            <span>Рецептура:</span>
            <Select aria-label="Рецептура для заявки" value={order.recipe_id || ""} placeholder="действующая на день отгрузки"
              options={approved.map((r: any) => ({ value: r.id, label: `версия ${r.version} (${r.plant_name}${r.active_for.length ? ", действует" : ""})` }))}
              onChange={async (e) => {
                try { await api.post(`/orders/${order.id}/recipe`, { recipe_id: e.target.value || null }); toast.ok("Рецептура заявки сохранена"); onChanged(); } catch (err) { toast.error(err); }
              }} />
            {!approved.length && <span className="muted">для марки нет утверждённой рецептуры</span>}
          </div>
        )}
        {canPassport && (pass.data ? (
          <div className="small">{p ? <>Паспорт №{p.number} от {fmtDate(p.issued_at)}{p.issued_by ? `, выдал ${p.issued_by}` : ""}</> : order.progress.shipped_m3 > 0 ? "Паспорт ещё не выдан" : "Паспорт выдаётся после первой отгрузки"}</div>
        ) : <span className="small muted">…</span>)}
      </div>
      {canPassport && (
        <div className="row tight">
          {p && <Button size="small" onClick={() => print(p.id)}>Паспорт качества</Button>}
          {issue && !p && <Button size="small" variant="primary" disabled={!(order.progress.shipped_m3 > 0)} onClick={() => make(false)}>Выдать паспорт качества</Button>}
          {issue && p && <Button size="small" variant="ghost" title="Новый номер с текущими данными, например после догрузки" onClick={() => make(true)}>Выдать заново</Button>}
        </div>
      )}
    </div>
  );
}

function Reschedule({ order, onClose, onDone }: { order: any; onClose: () => void; onDone: (id: string) => void }) {
  const toast = useToast();
  const local = new Date(order.desired_start);
  const [day, setDay] = useState(order.date);
  const [time, setTime] = useState(fmtTime(local));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [wholeSite, setWholeSite] = useState(false);
  const [days, setDays] = useState("1");
  // П-8 (22.10.2026): сразу видно, свободно ли новое время; если занято — свободные варианты, в т. ч. другая РБУ
  const [avail, setAvail] = useState<Availability | null>(null);
  const [checking, setChecking] = useState(false);
  const [choice, setChoice] = useState<Option | null>(null);
  const desired = /^\d{2}:\d{2}$/.test(time) && day ? zonedISO(day, time) : null;
  useEffect(() => {
    setChoice(null);
    if (wholeSite || !desired) { setAvail(null); return; }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const vol = order.progress?.remaining_m3 > 0 ? order.progress.remaining_m3 : order.volume_m3;
        setAvail(await api.post("/scheduling/availability", {
          site_id: order.site.id, volume_m3: vol, desired_start: desired, intensity_m3h: order.intensity_m3h || null,
          trip_m3: order.trip_m3 || null, plant_id: order.plant_id || null, exclude_order_id: order.id,
        }));
      } catch { setAvail(null); } finally { setChecking(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [desired, wholeSite]);
  const submit = async (opt?: any) => {
    setBusy(true);
    try {
      if (wholeSite) {
        // ФТ-2.5: сдвиг стройки — все заявки объекта с этой даты на N дней
        const r = await api.post("/orders/reschedule-site", { site_id: order.site.id, from_date: order.date, days: Number(days), reason });
        const failed = r.failed?.length ? `; не удалось: ${r.failed.map((f: any) => `№${f.number} — ${f.message}`).join("; ")}` : "";
        if (r.failed?.length) toast.error(`Перенесено заявок: ${r.moved.length}${failed}`);
        else toast.ok(`Перенесено заявок объекта: ${r.moved.length}`);
        onDone(order.id);
        return;
      }
      const body: any = { version: order.version, new_start: opt ? opt.arrival_start : zonedISO(day, time), reason };
      if (opt) { body.unit_id = opt.unit_id; body.arrival = opt.arrival_start; }
      const r = await api.post(`/orders/${order.id}/reschedule`, body);
      toast.ok(r.id === order.id ? "Заявка перенесена, рейсы пересчитаны" : `Перенесена на другой день: заявка №${r.number}`);
      onDone(r.id);
    } catch (e) {
      if (e instanceof ApiError && e.details.availability) { setAvail(e.details.availability); toast.error("Время занято — выберите свободный вариант"); }
      else toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Перенос заявки №${order.number}`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Не переносить</Button>
      <Button variant="primary" disabled={busy || reason.trim().length < 2 || (wholeSite && !Number(days))} onClick={() => submit(choice || undefined)}>{wholeSite ? "Перенести все заявки объекта" : choice ? `Перенести на ${fmtTime(choice.arrival_start)}, ${choice.unit_name}` : "Перенести"}</Button>
    </>}>
      <label className="check" style={{ marginBottom: 10 }}>
        <input type="checkbox" checked={wholeSite} onChange={(e) => setWholeSite(e.target.checked)} />
        Сдвиг стройки: перенести все заявки объекта «{order.site.name}» с {fmtDate(order.date)}
      </label>
      <div className="form-grid">
        {wholeSite ? (
          <Field label="На сколько дней" hint="Отрицательное число — раньше. Переносятся новые и подтверждённые заявки"><Input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} /></Field>
        ) : (
          <>
            <Field label="Дата"><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} /></Field>
            <Field label="Время"><Input type="time" step={300} value={time} onChange={(e) => setTime(e.target.value)} /></Field>
          </>
        )}
        <Field label="Причина" wide hint="Например: прораб просит позже, бригада не готова"><Input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </div>
      {!wholeSite && (
        <div className="stack tight" style={{ marginTop: 10 }}>
          {checking && <div className="spinner" aria-label="Проверяю окно" />}
          {avail && (avail.free && avail.requested ? (
            <div className="alert ok"><div className="t">{fmtTime(avail.requested.arrival_start)} свободно: {avail.requested.plant_name}, {avail.requested.unit_name}</div></div>
          ) : (
            <div className="alert"><div className="t">На это время окно занято</div><div className="d">{avail.reason || "Выберите свободный вариант"}</div></div>
          ))}
          {avail && !avail.free && <OptionList options={avail.options} choice={choice} onPick={setChoice} />}
        </div>
      )}
    </Modal>
  );
}

function TripsTable({ order, onChanged }: { order: any; onChanged: () => void }) {
  const { me, refs } = useCtx();
  const toast = useToast();
  const [assignFor, setAssignFor] = useState<Trip | null>(null);
  const [lateFor, setLateFor] = useState<{ trip: Trip; status: string } | null>(null);
  const [cancelFor, setCancelFor] = useState<Trip | null>(null);
  const [redirectFor, setRedirectFor] = useState<Trip | null>(null);
  const beforeDeparture = (t: Trip) => ["planned", "assigned", "queued", "loading"].includes(t.status);
  const dispatch = can.dispatch(me.role);
  const next = (t: Trip) => {
    const i = TRIP_ORDER.indexOf(t.status);
    if (t.status === "planned") return null;
    return i >= 0 && i < TRIP_ORDER.length - 1 ? TRIP_ORDER[i + 1] : null;
  };
  const setStatus = async (t: Trip, status: string, extra: Record<string, unknown> = {}) => {
    try {
      await api.post(`/trips/${t.id}/status`, { version: t.version, status, ...extra });
      toast.ok(`Рейс ${t.seq}: ${TRIP_STATUS[status].toLowerCase()}`);
      onChanged();
    } catch (e) {
      if (e instanceof ApiError && e.details.code === "delay_reason_required") setLateFor({ trip: t, status });
      else toast.error(e);
    }
  };
  const withWaybill = order.trips.filter((t: Trip) => t.waybill_number && t.status !== "cancelled").length;
  return (
    <div className="table-wrap">
      {withWaybill > 1 && (
        <div className="row" style={{ justifyContent: "flex-end", padding: "6px 0" }}>
          <Button size="small" onClick={() => api.open(`/orders/${order.id}/waybills`).catch((e) => toast.error(e))}>Печать всех накладных ({withWaybill})</Button>
        </div>
      )}
      <table className="table">
        <thead>
          <tr><th>№</th><th>РБУ</th><th>Погрузка</th><th>Прибытие</th><th className="num">м³</th><th>Машина и водитель</th><th>Статус</th><th>Сроки</th><th /></tr>
        </thead>
        <tbody>
          {order.trips.map((t: Trip) => (
            <tr key={t.id} style={{ opacity: t.status === "cancelled" ? 0.5 : 1 }}>
              <td className="num">{t.seq}</td>
              <td>{t.unit_name}</td>
              <td className="tabular">{fmtTime(t.slot_start)}{t.load_started_at && <div className="small muted">факт {fmtTime(t.load_started_at)}</div>}</td>
              <td className="tabular">{fmtTime(t.planned_arrival)}{t.arrived_at ? <div className="small muted">факт {fmtTime(t.arrived_at)}</div>
                : t.eta && ["departed", "loading", "queued"].includes(t.status) ? <div className="small muted">ожид. ~{fmtTime(t.eta)}</div> : null}</td>
              <td className="num">{fmtNum(t.actual_volume_m3 ?? t.volume_m3)}</td>
              <td>
                {t.vehicle_plate || <span className="muted">—</span>}
                {t.driver_name && (
                  <div className="small muted">
                    {t.driver_name}
                    {t.driver_phone && <>, <a href={telHref(t.driver_phone)}>{fmtPhone(t.driver_phone)}</a></>}
                  </div>
                )}
                {t.last_gps_at && t.status !== "completed" && <div className="small muted" title="Последние координаты с телефона водителя">GPS: {gpsAgo(t.last_gps_at)}</div>}
                {dispatch && ["planned", "assigned", "queued"].includes(t.status) && (
                  <Button size="small" variant="ghost" onClick={() => setAssignFor(t)}>{t.vehicle_plate ? "Сменить" : "Назначить"}</Button>
                )}
              </td>
              <td><span className="small">{TRIP_STATUS[t.status]}</span>{t.cancel_reason && <div className="small muted">{t.cancel_reason}</div>}</td>
              <td>
                {t.timing_level === "late" && <span className="tag s-conflict">+{t.deviation_minutes} мин</span>}
                {t.timing_level === "risk" && <span className="tag s-transit">риск +{t.deviation_minutes} мин</span>}
                {t.timing_level === "ok" && t.status !== "cancelled" && <span className="tag s-done">в норме</span>}
                {t.idle_minutes > 0 && <div><span className="tag s-transit" title="Время на объекте сверх плановой разгрузки и допуска">простой +{t.idle_minutes} мин</span></div>}
                {t.delay_reason_id && <div className="small muted">{refs.delayReasons.find((r) => r.id === t.delay_reason_id)?.name}</div>}
              </td>
              <td className="right">
                <div className="row tight" style={{ justifyContent: "flex-end" }}>
                  {dispatch && next(t) && t.status !== "cancelled" && (
                    <Button size="small" onClick={() => setStatus(t, next(t)!)}>{TRIP_STATUS[next(t)!]}</Button>
                  )}
                  {dispatch && t.status !== "cancelled" && t.status !== "completed" && (
                    <Button size="small" variant="ghost" title="Клиент сменил объект — рейс к другой заявке той же марки" onClick={() => setRedirectFor(t)}>На другую заявку</Button>
                  )}
                  {dispatch && beforeDeparture(t) && t.status !== "loading" && (
                    <Button size="small" variant="ghost" onClick={() => setCancelFor(t)}>Отменить рейс</Button>
                  )}
                  {t.waybill_number && (
                    <Button size="small" variant="ghost" onClick={() => api.open(`/trips/${t.id}/waybill`).catch((e) => toast.error(e))}>Накладная №{t.waybill_number}</Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {cancelFor && (
        <ReasonDialog title={`Отменить рейс ${cancelFor.seq}?`} action="Отменить рейс" danger onCancel={() => setCancelFor(null)}
          onConfirm={async (reason) => { await setStatus(cancelFor, "cancelled", { reason }); setCancelFor(null); }} />
      )}
      {redirectFor && <RedirectTrip trip={redirectFor} order={order} onClose={() => setRedirectFor(null)} onDone={() => { setRedirectFor(null); onChanged(); }} />}
      {assignFor && <AssignVehicle trip={assignFor} onClose={() => setAssignFor(null)} onDone={() => { setAssignFor(null); onChanged(); }} />}
      {lateFor && (
        <DelayDialog trip={lateFor.trip} onClose={() => setLateFor(null)}
          onConfirm={async (reasonId, comment) => { await setStatus(lateFor.trip, lateFor.status, { delay_reason_id: reasonId, delay_comment: comment }); setLateFor(null); }} />
      )}
    </div>
  );
}

/** ФТ-5.4: рейс к другой заявке (клиент сменил объект) — только заявки той же марки, ещё не закрытые. */
function RedirectTrip({ trip, order, onClose, onDone }: { trip: Trip; order: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const list = useAsync<any[]>(() => api.get("/orders", { date: order.date, limit: 500 }), [order.date]);
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const fits = (list.data || []).filter((x) => x.id !== order.id && x.grade_id === order.grade.id && ["new", "confirmed", "in_progress"].includes(x.status));
  return (
    <Modal title={`Рейс ${trip.seq}: на другую заявку`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!target || reason.trim().length < 2} onClick={async () => {
        try { await api.post(`/trips/${trip.id}/redirect`, { version: trip.version, order_id: target, reason }); toast.ok("Рейс перенаправлен"); onDone(); } catch (e) { toast.error(e); }
      }}>Перенаправить</Button>
    </>}>
      {!list.data ? <Loading error={list.error} /> : fits.length === 0 ? (
        <p className="muted">На {fmtDate(order.date)} нет других открытых заявок с маркой {order.grade.code}.</p>
      ) : (
        <div className="stack tight">
          <p className="small muted">Подходят заявки этого дня с той же маркой ({order.grade.code}). Рейс уйдёт к выбранной заявке со своей машиной и временем.</p>
          {fits.map((x) => (
            <label key={x.id} className="row" style={{ padding: "6px 4px", cursor: "pointer" }}>
              <input type="radio" name="target" checked={target === x.id} onChange={() => setTarget(x.id)} />
              <b>№{x.number}</b><span>{x.customer_name}, {x.site_name}</span>
              <span className="small muted grow right">{fmtTime(x.desired_start)}, {fmtNum(x.volume_m3)} м³</span>
            </label>
          ))}
          <Field label="Причина" hint="Например: клиент сменил объект"><Input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        </div>
      )}
    </Modal>
  );
}

/** Правка заявки. Для заявки из регулярного шаблона — «только эта» или «эта и все будущие» (ФТ-2.4). */
function EditOrder({ order, onClose, onDone }: { order: any; onClose: () => void; onDone: () => void }) {
  const { refs } = useCtx();
  const toast = useToast();
  const cust = useAsync<any>(() => api.get(`/customers/${order.customer.id}`), [order.customer.id]);
  const [v, setV] = useState({
    volume_m3: String(order.volume_m3), grade_id: order.grade.id, contact_id: order.contact?.id || "",
    intensity_m3h: order.intensity_m3h ? String(order.intensity_m3h) : "", priority: String(order.priority || 0),
    comment_driver: order.comment_driver || "", comment_operator: order.comment_operator || "", comment_client: order.comment_client || "",
    scope: "this", reason: "",
  });
  const [busy, setBusy] = useState(false);
  const [options, setOptions] = useState<any[] | null>(null);
  const set = (p: Partial<typeof v>) => setV({ ...v, ...p });
  const save = async (opt?: any) => {
    const body: Record<string, unknown> = { version: order.version, scope: v.scope, reason: v.reason || null };
    if (opt) { body.unit_id = opt.unit_id; body.arrival = opt.arrival_start; }
    const num = (x: string) => (x.trim() === "" ? null : Number(x.replace(",", ".")));
    if (num(v.volume_m3) !== order.volume_m3) body.volume_m3 = num(v.volume_m3);
    if (v.grade_id !== order.grade.id) body.grade_id = v.grade_id;
    if ((v.contact_id || null) !== (order.contact?.id || null)) body.contact_id = v.contact_id || null;
    if (num(v.intensity_m3h) !== (order.intensity_m3h ?? null)) body.intensity_m3h = num(v.intensity_m3h);
    if (Number(v.priority) !== (order.priority || 0)) body.priority = Number(v.priority) || 0;
    for (const k of ["comment_driver", "comment_operator", "comment_client"] as const) if ((v[k] || null) !== (order[k] || null)) body[k] = v[k] || null;
    if (Object.keys(body).length <= (opt ? 5 : 3)) { onClose(); return; }
    setBusy(true);
    try { await api.patch(`/orders/${order.id}`, body); toast.ok(v.scope === "future" ? "Заявка и все будущие по шаблону изменены" : "Заявка изменена"); onDone(); }
    catch (e) {
      // больший объём не помещается в прежнее окно — предлагаем ближайшие варианты, как при переносе
      if (e instanceof ApiError && e.details.availability) setOptions(e.details.availability.options);
      else toast.error(e);
    } finally { setBusy(false); }
  };
  return (
    <Modal title={`Изменить заявку №${order.number}`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={busy || !Number(v.volume_m3.replace(",", "."))} onClick={() => save()}>Сохранить</Button>
    </>}>
      <div className="form-grid">
        <Field label="Объём, м³" hint="Рейсы пересчитаются"><Input inputMode="decimal" value={v.volume_m3} onChange={(e) => set({ volume_m3: e.target.value })} /></Field>
        <Field label="Марка"><Select value={v.grade_id} onChange={(e) => set({ grade_id: e.target.value })} options={refs.grades.map((g) => ({ value: g.id, label: g.code + (g.requires_lab_approval ? " (лаборатория)" : "") }))} /></Field>
        <Field label="Интенсивность приёма, м³/ч"><Input inputMode="decimal" value={v.intensity_m3h} onChange={(e) => set({ intensity_m3h: e.target.value })} /></Field>
        <Field label="Контакт на объекте"><Select value={v.contact_id} onChange={(e) => set({ contact_id: e.target.value })} placeholder="—"
          options={(cust.data?.contacts || []).map((c: any) => ({ value: c.id, label: `${c.full_name}${c.position ? ", " + c.position : ""}` }))} /></Field>
        <Field label="Приоритет" hint="Выше — важнее при нехватке окон"><Input inputMode="numeric" value={v.priority} onChange={(e) => set({ priority: e.target.value })} /></Field>
        <Field label="Комментарий водителю"><Input value={v.comment_driver} onChange={(e) => set({ comment_driver: e.target.value })} /></Field>
        <Field label="Комментарий оператору" wide><Input value={v.comment_operator} onChange={(e) => set({ comment_operator: e.target.value })} /></Field>
        {order.recurring_id && (
          <Field label="Что изменить" wide hint="«Все будущие» меняет регулярный шаблон: будущие дни пересоздаются по нему (кроме изменённых отдельно)">
            <Select value={v.scope} onChange={(e) => set({ scope: e.target.value })} options={[{ value: "this", label: "Только эту заявку" }, { value: "future", label: "Эту и все будущие по шаблону" }]} />
          </Field>
        )}
        <Field label="Причина" wide hint="Попадёт в историю заявки"><Input value={v.reason} onChange={(e) => set({ reason: e.target.value })} /></Field>
      </div>
      {options && (
        <div className="stack tight" style={{ marginTop: 10 }}>
          <div className="alert"><div className="t">С новым объёмом прежнее окно занято — выберите время</div></div>
          {options.length === 0 && <p className="small muted">Свободных вариантов рядом нет. Уменьшите объём или перенесите заявку.</p>}
          {options.map((o) => (
            <Button key={o.unit_id + o.arrival_start} disabled={busy} onClick={() => save(o)}>{fmtTime(o.arrival_start)}, {o.plant_name}, {o.unit_name}</Button>
          ))}
        </div>
      )}
      <p className="small muted">Время и дату меняйте кнопкой «Перенести».</p>
    </Modal>
  );
}

/** Встреча 15.10: машину выбирает диспетчер сам — система ничего не советует и не сортирует «лучших».
 *  Список — рабочий парк (машины, которые сейчас работают у нас) по госномеру; цвет — где машина сейчас. */
function AssignVehicle({ trip, onClose, onDone }: { trip: Trip; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [all, setAll] = useState(false);
  const [q, setQ] = useState("");
  const list = useAsync<any[]>(() => api.get(`/trips/${trip.id}/vehicles`, { all: all ? "true" : undefined }), [trip.id, all]);
  const [vid, setVid] = useState("");
  const [driver, setDriver] = useState(""); // водитель на этот рейс: по умолчанию закреплённый за машиной
  const pick = (v: any) => { setVid(v.id); setDriver(v.driver_name || ""); };
  const needle = q.trim().toLowerCase().replace(/\s+/g, "");
  const rows = (list.data || []).filter((v) => !needle || `${v.plate}${v.driver_name || ""}${v.carrier || ""}`.toLowerCase().replace(/\s+/g, "").includes(needle));
  return (
    <Modal title={`Машина на рейс ${trip.seq}`} onClose={onClose} actions={<>
      {trip.vehicle_plate && <Button variant="danger" onClick={async () => { try { await api.post(`/trips/${trip.id}/assign`, { version: trip.version, vehicle_id: null }); onDone(); } catch (e) { toast.error(e); } }}>Снять машину</Button>}
      <Button onClick={onClose}>Закрыть</Button>
      <Button variant="primary" disabled={!vid} onClick={async () => {
        try { await api.post(`/trips/${trip.id}/assign`, { version: trip.version, vehicle_id: vid, driver_name: driver.trim() || null }); toast.ok("Машина назначена"); onDone(); } catch (e) { toast.error(e); }
      }}>Назначить</Button>
    </>}>
      <div className="stack tight">
        <p className="small muted" style={{ margin: 0 }}>Погрузка {fmtTime(trip.slot_start)}, объём {fmtNum(trip.volume_m3)} м³. {all ? "Все машины" : "Машины, которые сейчас работают у нас"}, по госномеру.</p>
        <div className="row">
          <Input className="grow" placeholder="Госномер, водитель или перевозчик" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Поиск машины" />
          <label className="check small"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />все машины</label>
        </div>
        {!list.data ? <Loading error={list.error} /> : (
          <div className="stack tight" style={{ maxHeight: 360, overflow: "auto" }}>
            {rows.length === 0 && <p className="muted small">Нет машин. {all ? "" : "Отметьте «все машины» или добавьте машину в работу на странице «Машины»."}</p>}
            {rows.map((v) => (
              <label key={v.id} className="row" style={{ padding: "6px 4px", opacity: v.available ? 1 : 0.55, cursor: v.available ? "pointer" : "not-allowed" }}>
                <input type="radio" name="veh" disabled={!v.available} checked={vid === v.id} onChange={() => pick(v)} />
                <PlaceDot place={v.place} />
                <b className="num">{v.plate}</b>
                <span className="small">{v.drum_m3} м³, {v.owner === "own" ? "свой" : `наёмный${v.carrier ? ` (${v.carrier})` : ""}`}{v.driver_name ? `, ${v.driver_name}` : ", водитель не указан"}</span>
                <span className="small muted grow right">{!v.fits ? "мал барабан" : v.busy ? <>занята: {v.busy_with}{v.busy_trip && <> · <a className="link" href={`?order=${v.busy_trip.order_id}`} onClick={(e) => { e.preventDefault(); setQuery({ order: v.busy_trip.order_id }); onClose(); }}>открыть</a></>}</> : !v.on_duty ? "не в рабочем парке" : ""}</span>
              </label>
            ))}
          </div>
        )}
        {vid && (
          <Field label="Водитель на этот рейс" hint="Если сегодня на машине сменщик — впишите его, в карточке машины ничего не изменится">
            <Input value={driver} onChange={(e) => setDriver(e.target.value)} placeholder="ФИО водителя" />
          </Field>
        )}
      </div>
    </Modal>
  );
}

function DelayDialog({ trip, onClose, onConfirm }: { trip: Trip; onClose: () => void; onConfirm: (reasonId: string, comment: string) => Promise<void> }) {
  const { refs } = useCtx();
  const [rid, setRid] = useState("");
  const [comment, setComment] = useState("");
  return (
    <Modal title={`Рейс ${trip.seq}: опоздание сверх допуска`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!rid} onClick={() => onConfirm(rid, comment)}>Сохранить с причиной</Button>
    </>}>
      <Field label="Причина задержки">
        <Select value={rid} onChange={(e) => setRid(e.target.value)} placeholder="Выберите причину"
          options={refs.delayReasons.map((r) => ({ value: r.id, label: `${r.name} (${r.side === "client" ? "клиент" : "завод"})` }))} />
      </Field>
      <Field label="Комментарий"><Input value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
    </Modal>
  );
}

/** ФТ-2.8: файлы к заявке — скан письма, фото, спецификация. */
function Files({ orderId, canEdit }: { orderId: string; canEdit: boolean }) {
  const toast = useToast();
  const list = useAsync<any[]>(() => api.get(`/orders/${orderId}/attachments`), [orderId]);
  const [busy, setBusy] = useState(false);
  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    for (const f of Array.from(files)) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: файл больше 15 МБ`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try { await api.post(`/orders/${orderId}/attachments`, fd); } catch (e) { toast.error(e); }
    }
    setBusy(false);
    list.reload();
  };
  const size = (b: number) => (b > 1024 * 1024 ? `${fmtNum(b / 1024 / 1024, 1)} МБ` : `${Math.max(1, Math.round(b / 1024))} КБ`);
  return (
    <div className="stack tight">
      {canEdit && (
        <label className={`btn${busy ? " disabled" : ""}`} style={{ alignSelf: "flex-start" }}>Приложить файл
          <input type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx,.txt,.eml,.msg"
            onChange={(e) => { void upload(e.target.files); e.target.value = ""; }} />
        </label>
      )}
      {!list.data ? <Loading error={list.error} /> : list.data.length === 0 ? <p className="muted">Файлов нет. Сюда кладут скан письма клиента, фото, спецификацию.</p> : (
        <table className="table">
          <thead><tr><th>Файл</th><th className="num">Размер</th><th>Кто приложил</th><th /></tr></thead>
          <tbody>{list.data.map((a) => (
            <tr key={a.id}>
              <td><button type="button" className="linkbtn" onClick={() => api.open(a.url.replace("/api/v1", ""), undefined, a.content_type.startsWith("image/") || a.content_type === "application/pdf" ? undefined : a.name).catch(toast.error)}>{a.name}</button></td>
              <td className="num small">{size(a.size_bytes)}</td>
              <td className="small">{a.author || "—"}, {fmtDate(a.created_at)}</td>
              <td className="right">{canEdit && <Button size="small" variant="ghost" onClick={async () => {
                try { await api.del(`/orders/attachments/${a.id}`); toast.ok("Файл убран из заявки"); list.reload(); } catch (e) { toast.error(e); }
              }}>Убрать</Button>}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      <p className="small muted">PDF, фото, Word, Excel, письма (EML, MSG), до 15 МБ, до 20 файлов к заявке.</p>
    </div>
  );
}

function History({ id }: { id: string }) {
  const h = useAsync<AuditRow[]>(() => api.get(`/orders/${id}/history`), [id]);
  if (!h.data) return <Loading error={h.error} />;
  if (h.data.length === 0) return <p className="muted">Изменений пока нет.</p>;
  return <AuditTable rows={h.data} />;
}

function Tasks({ orderId }: { orderId: string }) {
  const toast = useToast();
  const list = useAsync<any[]>(() => api.get("/tasks", { status: "" }), [orderId]);
  const [title, setTitle] = useState("");
  const [closing, setClosing] = useState<TaskRow | null>(null);
  const mine = (list.data || []).filter((t) => t.order_id === orderId);
  return (
    <div className="stack">
      <div className="row">
        <Input placeholder="Например: перезвонить прорабу в 16:00" value={title} onChange={(e) => setTitle(e.target.value)} className="grow" />
        <Button disabled={title.trim().length < 2} onClick={async () => {
          try { await api.post("/tasks", { kind: "call_back", title, order_id: orderId }); setTitle(""); list.reload(); } catch (e) { toast.error(e); }
        }}>Добавить задачу</Button>
      </div>
      {mine.length === 0 && <p className="muted">Задач по заявке нет. Задачи видит следующая смена.</p>}
      {mine.map((t) => (
        <div key={t.id}>
          <label className="check">
            <input type="checkbox" checked={t.status === "done"} onChange={async (e) => {
              if (e.target.checked) { setClosing(t); return; }
              try { await api.patch(`/tasks/${t.id}`, { status: "open" }); toast.ok("Задача снова открыта"); } catch (err) { toast.error(err); }
              list.reload();
            }} />
            <span style={{ textDecoration: t.status === "done" ? "line-through" : "none" }}>{t.title}</span>
          </label>
          <TaskDone t={t} />
        </div>
      ))}
      {closing && <CloseTaskDialog task={closing} onClose={() => setClosing(null)} onDone={() => { setClosing(null); list.reload(); }} />}
    </div>
  );
}

function gpsAgo(at: string): string {
  const min = Math.round((Date.now() - new Date(at).getTime()) / 60000);
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  return `в ${fmtTime(at)}`;
}

/** Ссылка отслеживания для клиента (Э-15): диспетчер копирует и отправляет сам — в мессенджер или SMS. */
function ClientLink({ orderId, number, onClose }: { orderId: string; number: number; onClose: () => void }) {
  const toast = useToast();
  const q = useAsync<{ token: string }>(() => api.post(`/orders/${orderId}/tracking-link`), [orderId]);
  const url = q.data ? `${location.origin}${location.pathname}#/t/${q.data.token}` : "";
  const text = `Заказ бетона №${number}: где машины и когда приедут — ${url}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.ok("Скопировано — вставьте клиенту в мессенджер или SMS"); }
    catch { toast.error("Не удалось скопировать — выделите текст и скопируйте вручную"); }
  };
  return (
    <Modal title={`Ссылка для клиента, заявка №${number}`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Закрыть</Button>
      <Button variant="primary" disabled={!url} onClick={copy}>Скопировать</Button>
    </>}>
      {!q.data ? <Loading error={q.error} onRetry={q.reload} /> : (
        <div className="stack tight">
          <p className="small muted" style={{ margin: 0 }}>Клиент откроет без входа: статус заказа, госномера машин, время прибытия, телефоны водителя и диспетчерской. Цен и других заявок там нет.</p>
          <textarea className="input" rows={3} readOnly value={text} onFocus={(e) => e.currentTarget.select()} />
          <a className="small" href={url} target="_blank" rel="noreferrer">Открыть как клиент</a>
        </div>
      )}
    </Modal>
  );
}
